import { getText, getValue } from 'lib/value'
import {
    actionTokenPromptMatchesAdjacentContent,
    clampActionTokenPromptForDisplay,
} from './actionTokenPromptDisplay'

const normalizeActionTag = value =>
    String(value || '')
        .trim()
        .replace(/^!+/, '')
        .toLowerCase()

const actionRequestIds = action =>
    [
        action?.id,
        action?.client_id,
        action?.ai_action_id,
        action?.actionId,
        action?.action_id,
        action?.action_request_id,
    ]
        .filter(id => id != null && id !== '')
        .map(id => String(id))

const actionPromptText = source => {
    const prompt =
        source?.inputs?.text?.value ||
        source?.textInputValue ||
        source?.promptContent ||
        source?.prompt_content ||
        source?.prompt ||
        ''
    return typeof prompt === 'string' ? prompt.trim() : ''
}

const actionRequestMatchesToken = (action, attrs, tokenIndex, actionRequests) => {
    if (!action || !attrs) return false

    const tokenActionId =
        attrs.ai_action_id || attrs.actionId || attrs.action_id || attrs.action_request_id || null
    const requestActionIds = actionRequestIds(action)
    if (tokenActionId && requestActionIds.length) {
        return requestActionIds.includes(String(tokenActionId))
    }

    const tokenTag = normalizeActionTag(attrs.tag)
    const actionTag = normalizeActionTag(action.tag)
    if (!tokenTag || tokenTag !== actionTag) return false

    const sameTagRequests = actionRequests.filter(
        candidate => normalizeActionTag(candidate?.tag) === tokenTag,
    )
    if (sameTagRequests.length === 1) return sameTagRequests[0] === action

    return actionRequests[tokenIndex] === action
}

// The label and (policy-filtered) prompt for one action token, kept separate so
// surfaces that render the token as its own element — the quote line's chip —
// share this privacy policy instead of re-deriving it.
const actionTokenDisplayParts = ({ answer, op, tokenIndex, priorContent, followingContent }) => {
    const attrs = op?.attributes?.actionTagAttrs
    if (!attrs || typeof attrs !== 'object') return null

    const tag = normalizeActionTag(attrs.tag || op.insert)
    const displayName =
        typeof attrs.displayName === 'string'
            ? attrs.displayName.trim()
            : typeof attrs.display_name === 'string'
            ? attrs.display_name.trim()
            : ''
    const label = displayName || (tag ? `!${tag}` : String(op.insert || '').trim())
    const actionRequests = Array.isArray(answer?.action_requests) ? answer.action_requests : []
    const matchedAction = actionRequests.find(action =>
        actionRequestMatchesToken(action, attrs, tokenIndex, actionRequests),
    )
    // Structured action metadata is private by default and WYSIWYG for everyone
    // (owner included): only an explicitly shared action contributes its prompt
    // to display text — this keeps collapse/height estimation in step with the
    // rendered bare token. Legacy answers without structured requests keep the
    // delta-attrs fallback (their prompt is ordinary visible body text).
    const prompt = actionRequests.length
        ? matchedAction?.metadata_public === true
            ? actionPromptText(matchedAction) || actionPromptText(attrs)
            : ''
        : actionPromptText(attrs)

    if (
        !prompt ||
        actionTokenPromptMatchesAdjacentContent(prompt, priorContent, followingContent)
    ) {
        return { label, prompt: '', displayText: label }
    }
    // Long prompts render as the clamped generation card, so the display/estimation text
    // carries the same clamped form (collapse decisions + reserved heights track the render).
    const clampedPrompt = clampActionTokenPromptForDisplay(prompt)
    return {
        label,
        prompt: clampedPrompt,
        displayText: `${label} ${clampedPrompt}`.trim(),
    }
}

const actionTokenDisplayText = args => actionTokenDisplayParts(args)?.displayText ?? null

// The display projection split into its pieces: the action tokens as labels
// (plus whatever prompt policy allows) and the body text WITHOUT those labels.
// getAnswerDisplayText is this same walk joined back into one string, so both
// surfaces resolve tokens, prompts, and privacy identically.
export const getAnswerDisplayParts = answer => {
    const contentFallback = () =>
        answer?.content_preview || answer?.display_content || answer?.content || ''

    try {
        const value = getValue(answer)
        const fallback = getText(value) || contentFallback()

        if (!value || typeof value === 'string' || !Array.isArray(value.ops)) {
            return { tokens: [], text: fallback, displayText: fallback, fallback }
        }

        let tokenIndex = -1
        // `parts` keeps the original joined projection (labels included), both
        // for getAnswerDisplayText and because priorContent is measured against
        // it when deciding whether a prompt duplicates adjacent body text.
        const parts = []
        // The prose ops are collected and read through getText ONCE at the end:
        // getText trims what it is given, so extracting per-op would eat the
        // spaces that separate a mention, a bold run, or a paragraph from the
        // words around it.
        const textOps = []
        const tokens = []
        value.ops.forEach((op, opIndex) => {
            if (op?.attributes?.actionTagAttrs) {
                tokenIndex += 1
                const token = actionTokenDisplayParts({
                    answer,
                    op,
                    tokenIndex,
                    priorContent: parts.join(''),
                    followingContent: getText({ ops: value.ops.slice(opIndex + 1) }) || '',
                })
                if (token?.displayText) {
                    tokens.push(token)
                    parts.push(token.displayText)
                    // The label belongs to the token, not the prose.
                    if (token.prompt) textOps.push({ insert: ` ${token.prompt} ` })
                }
                return
            }

            parts.push(getText({ ops: [op] }) || '')
            textOps.push(op)
        })

        return {
            tokens,
            text: (getText({ ops: textOps }) || '').replace(/\s+/g, ' ').trim(),
            displayText: parts.join('').trim() || fallback,
            fallback,
        }
    } catch {
        const fallback = contentFallback()
        return { tokens: [], text: fallback, displayText: fallback, fallback }
    }
}

export const getAnswerDisplayText = answer => getAnswerDisplayParts(answer).displayText
